<script setup lang="ts">
import { courseCapabilities, courseDefinitions, courseDetail, courseOutline } from '../data/landing.content'
import { useLandingScroll } from '../composables/useLandingScroll'
import WlIcon from './WlIcon.vue'

/**
 * Seccion "Cursos" (ancla `#cursos`).
 *
 * Sustituye a la antigua seccion "Plataforma". El copy y el mockup se apoyan
 * en el modulo de cursos real: un curso se ordena en secciones, temas y
 * actividades; cada actividad es una leccion o un examen; y el avance por
 * actividad queda registrado.
 *
 * El mockup es una ilustracion del interior de un curso: por eso no publica
 * cifras de preguntas, horas ni estudiantes.
 */
const { registerReveal } = useLandingScroll()
</script>

<template>
  <section class="wl-preview-section" id="cursos">
    <div class="container">
      <div class="section-head reveal" :ref="registerReveal">
        <div class="section-tag">Cursos</div>
        <h2 class="section-title">Todo lo que pasa <span class="gradient-text">dentro de un curso</span></h2>
        <p class="section-desc">
          Un curso reúne el temario completo, las lecciones del profesor y los exámenes de práctica en un solo lugar.
        </p>
      </div>

      <!-- Bloque A: que es un curso. El encabezado va a ancho completo -->
      <div class="wl-crs-block wl-crs-whatis--head">
        <div class="wl-crs-block-head reveal" :ref="registerReveal">
          <span class="section-tag">Qué es</span>
          <h3 class="wl-crs-block-title">
            Un curso es un <span class="gradient-text">recorrido con orden</span>
          </h3>
          <p class="wl-crs-block-desc">
            No es una lista de videos sueltos. Es un temario que te va marcando el camino y te dice en qué vas.
          </p>
        </div>
      </div>

      <!-- Showcase: definiciones (vertical) + mockup del interior de un curso -->
      <div class="wl-crs-showcase reveal" :ref="registerReveal">
        <div class="wl-crs-showcase-side">
          <div class="wl-crs-defs">
            <article
              v-for="(definition, index) in courseDefinitions"
              :key="definition.title"
              class="wl-crs-def reveal"
              :class="`reveal-d${index + 1}`"
              :ref="registerReveal"
            >
              <span class="wl-crs-icon">
                <WlIcon :name="definition.icon" />
              </span>
              <h4>{{ definition.title }}</h4>
              <p>{{ definition.description }}</p>
            </article>
          </div>
        </div>

        <div class="wl-crs-showcase-main">
          <!-- Mockup: como se ve un curso por dentro -->
          <div class="wl-browser-mockup wl-browser-mockup--crs">
            <div class="wl-bm-chrome">
              <div class="wl-bm-dots">
                <span class="wl-bd-r"></span>
                <span class="wl-bd-y"></span>
                <span class="wl-bd-g"></span>
              </div>
              <div class="wl-bm-url">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" />
                  <path d="M7 11V7a5 5 0 0110 0v4" />
                </svg>
                appmisionate.com/cursos
              </div>
              <div class="wl-bm-ctrl">
                <span></span><span></span><span></span>
              </div>
            </div>

            <div class="wl-crs-body">
              <!-- Temario del curso -->
              <div class="wl-crs-outline">
                <div class="wl-crs-outline-head">
                  <span class="wl-crs-outline-label">Temario</span>
                  <span class="wl-crs-outline-note">Ejemplo de curso</span>
                </div>

                <div v-for="unit in courseOutline" :key="unit.title" class="wl-crs-unit">
                  <h4 class="wl-crs-unit-title">{{ unit.title }}</h4>

                  <div v-for="theme in unit.themes" :key="theme.title" class="wl-crs-theme">
                    <p class="wl-crs-theme-title">{{ theme.title }}</p>

                    <div v-for="activity in theme.activities" :key="activity.title" class="wl-crs-activity">
                      <span class="wl-crs-activity-icon" :class="`wl-crs-activity-icon--${activity.kind}`">
                        <WlIcon :name="activity.kind === 'examen' ? 'list-ul' : 'play'" />
                      </span>
                      <div class="wl-crs-activity-body">
                        <p class="wl-crs-activity-title">{{ activity.title }}</p>
                        <div class="wl-crs-activity-meta">
                          <span class="wl-crs-kind" :class="`wl-crs-kind--${activity.kind}`">
                            {{ activity.kind === 'examen' ? 'Examen' : 'Lección' }}
                          </span>
                          <span v-for="feature in activity.features" :key="feature" class="wl-crs-feature">
                            <WlIcon :name="feature" />
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Actividad que se esta estudiando -->
              <div class="wl-crs-detail">
                <span class="wl-crs-detail-tag">{{ courseDetail.tag }}</span>
                <h3 class="wl-crs-detail-title">{{ courseDetail.title }}</h3>

                <div class="wl-crs-video">
                  <span class="wl-crs-video-play">
                    <WlIcon name="play" />
                  </span>
                  <span class="wl-crs-video-label">{{ courseDetail.videoLabel }}</span>
                </div>

                <ul class="wl-crs-detail-list">
                  <li v-for="item in courseDetail.items" :key="item.text" class="wl-crs-detail-item">
                    <WlIcon :name="item.icon" />
                    <span>{{ item.text }}</span>
                  </li>
                </ul>

                <div class="wl-crs-progress">
                  <span class="wl-crs-progress-label">{{ courseDetail.progressLabel }}</span>
                  <div class="wl-crs-progress-bar">
                    <span class="wl-crs-progress-fill"></span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Bloque B: que puedes hacer dentro de un curso -->
      <div class="wl-crs-block wl-crs-block--spaced">
        <div class="wl-crs-block-head reveal" :ref="registerReveal">
          <span class="section-tag">Qué puedes hacer</span>
          <h3 class="wl-crs-block-title">Ocho formas de <span class="gradient-text">avanzar</span></h3>
          <p class="wl-crs-block-desc">
            Cada actividad del curso te ofrece una forma distinta de aprender y de practicar antes del examen.
          </p>
        </div>

        <div class="wl-crs-caps reveal" :ref="registerReveal">
          <div v-for="capability in courseCapabilities" :key="capability.title" class="wl-crs-cap">
            <span class="wl-crs-icon wl-crs-icon--sm">
              <WlIcon :name="capability.icon" />
            </span>
            <div class="wl-crs-cap-body">
              <h5>{{ capability.title }}</h5>
              <p>{{ capability.description }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
